import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const faqCategories = [
  {
    category: 'General Questions',
    faqs: [
      {
        question: 'What areas do you serve?',
        answer: 'We serve St. Augustine, Jacksonville, Ponte Vedra, Nocatee, St. Johns County, and Palm Coast in Northeast Florida. If you\'re unsure if your area is covered, please contact us—we may still be able to help!',
      },
      {
        question: 'How long have you been in business?',
        answer: 'Our senior lead brings over 19 years of remodeling experience. Jax Remodeling Solutions combines this deep expertise with professional project management and high-end client service standards.',
      },
      {
        question: 'Are you licensed and insured?',
        answer: 'Yes, we are fully insured. Jax Remodeling Solutions LLC provides remodeling and specialty trade services as permitted. We do not offer services requiring a Florida General Contractor license at this time. Where needed, we can coordinate with properly licensed professionals.',
      },
      {
        question: 'Do you provide free estimates?',
        answer: 'Yes, we offer complimentary consultations and detailed estimates for all projects. Contact us to schedule your free estimate.',
      },
    ],
  },
  {
    category: 'Project Timeline & Process',
    faqs: [
      {
        question: 'How long does a typical kitchen remodel take?',
        answer: 'Timeline varies based on scope, but most kitchen remodels take 4-8 weeks. We provide a detailed, realistic schedule during the estimate phase so you know exactly what to expect.',
      },
      {
        question: 'How long does a bathroom remodel take?',
        answer: 'Most bathroom remodels take 2-5 weeks depending on the scope of work. We\'ll provide a specific timeline during your consultation.',
      },
      {
        question: 'What is your project process?',
        answer: 'Our process includes: 1) Initial phone consultation, 2) Site visit and scope alignment, 3) Detailed estimate and schedule, 4) Pre-start walkthrough, 5) Build phase with regular updates, and 6) Final walkthrough and closeout. Learn more on our Process page.',
      },
      {
        question: 'Can I live in my home during the remodel?',
        answer: 'In most cases, yes. We implement dust barriers, floor protection, and daily cleanup to minimize disruption. We\'ll discuss logistics specific to your project during the planning phase.',
      },
    ],
  },
  {
    category: 'Costs & Payment',
    faqs: [
      {
        question: 'How much does a kitchen remodel cost?',
        answer: 'Kitchen remodel costs vary widely based on scope, materials, and finishes. We provide detailed, itemized estimates after the site visit so you have complete clarity on pricing before work begins.',
      },
      {
        question: 'Do you require a deposit?',
        answer: 'Yes, we typically require a deposit to secure your project date and order materials. The specific payment schedule is outlined in your project agreement.',
      },
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept checks, credit cards, and bank transfers. Payment schedules are based on project milestones and outlined clearly in your agreement.',
      },
      {
        question: 'Are there any hidden fees?',
        answer: 'No. We believe in complete transparency. Your estimate includes all anticipated costs. If any changes arise during the project, we discuss them with you immediately and document any change orders clearly.',
      },
    ],
  },
  {
    category: 'Services & Materials',
    faqs: [
      {
        question: 'What services do you offer?',
        answer: 'We offer kitchen remodeling, bathroom remodeling, interior renovations, flooring, tile work, painting, trim/finish carpentry, drywall repair, door/window installation, deck repair, light demolition, punch list work, and rental property refreshes.',
      },
      {
        question: 'Do you help with design and material selection?',
        answer: 'Yes, we coordinate design elements and can guide you through material selection. We also work with your designer if you have one.',
      },
      {
        question: 'Can you match existing finishes in my home?',
        answer: 'Absolutely. We have extensive experience matching existing paint colors, textures, wood finishes, and other materials for seamless integration with your home.',
      },
      {
        question: 'Do you handle permits?',
        answer: 'For work that requires permits within our scope of services, we handle the permit process. We\'ll discuss permit requirements specific to your project during planning.',
      },
    ],
  },
  {
    category: 'Communication & Quality',
    faqs: [
      {
        question: 'How will you communicate with me during the project?',
        answer: 'We provide daily or weekly updates via your preferred method—phone, text, email, or in-person. You\'ll always know the status of your project and can reach us with questions anytime.',
      },
      {
        question: 'What if I need changes during the project?',
        answer: 'Changes happen, and we handle them professionally. Any scope changes are documented with clear pricing and timeline impacts before proceeding. No surprises.',
      },
      {
        question: 'How do you protect my home during construction?',
        answer: 'We use dust barriers, floor protection, and implement daily cleanup protocols. We treat your home with the same respect we\'d treat our own.',
      },
      {
        question: 'What is your warranty policy?',
        answer: 'We stand behind our work. Specific warranty terms are outlined in your project agreement. We\'re committed to your satisfaction and address any concerns promptly.',
      },
    ],
  },
];

export const FAQ = () => {
  return (
    <main data-testid="faq-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">Frequently Asked Questions</h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              Find answers to common questions about our services, process, and what to expect when working with Jax Remodeling Solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="space-y-16">
            {faqCategories.map((category, categoryIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: categoryIndex * 0.1 }}
              >
                <h2 className="font-heading text-2xl mb-6 pb-4 border-b border-gray-200">
                  {category.category}
                </h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.faqs.map((faq, faqIndex) => (
                    <AccordionItem
                      key={faqIndex}
                      value={`${categoryIndex}-${faqIndex}`}
                      className="border border-gray-200 px-6"
                    >
                      <AccordionTrigger className="text-left font-medium py-5 hover:no-underline hover:text-[#C6A87C]">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-24 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Still Have Questions?</h2>
            <p className="text-gray-600 mb-8">
              We're here to help. Contact us directly and we'll be happy to answer any questions you have about your project.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:9048636673"
                data-testid="faq-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#C6A87C] transition-colors"
              >
                <Phone className="w-4 h-4" />
                (904) 863-6673
              </a>
              <Link
                to="/contact"
                data-testid="faq-contact-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Request a free estimate and let's discuss your vision.
            </p>
            <Link
              to="/contact"
              data-testid="faq-estimate-btn"
              className="inline-flex items-center justify-center gap-3 bg-white text-[#0A0A0A] px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors"
            >
              Request Free Estimate
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
